import { observer } from "mobx-react";
import usePage from "../../../../../../components/layouts/base-page-layout/page-hook";
import { useCallback, useEffect, useRef, useState } from "react";
import { ConfirmModal } from "../../../../../../components/modal/components/confirm-modal/confirm-modal";
import useTranslation from "../../../../../../translation/hooks/translation-hook";
import currentPortalStore from "../../../../../../stores/current-portal-store";
import useNavigationBlocker from "../../../../../../hooks/navigation-blocker-hook";
import TranslationScope from "../../../../../../translation/translation-scope";
import EditorPageLayout from "../../../../../../components/layouts/editor-page/editor-page-layout";
import ListLayout from "../../../../../../components/layouts/list-layout/list-layout";
import ControlGroupWithPreview from "../../../../../../components/layouts/control-group-with-preview/control-group-with-preview";
import LocateOrderPreview from '../../../../../../images/previews/locate-order.png';
import LocateOrderGiftReturnPreview from '../../../../../../images/previews/locate-order-gift-return.svg';
import LocateOrderGiftCardEntry, { ILocateOrderGiftCardEntryRef } from "./locate-order-gift-card-entry";
import LocateOrderLocateOrderEntry, { ILocateOrderLocateOrderEntryRef } from "./locate-order-locate-order-entry";

const LocateOrderEditorPage: React.FC = observer(() => {
    const { setSubTitles } = usePage();
    const [isShowUnsaved, setIsShowUnsaved] = useState(false);
    const giftCardEntryRef = useRef<ILocateOrderGiftCardEntryRef>(null);
    const locateOrderEntryRef = useRef<ILocateOrderLocateOrderEntryRef>(null);

    const giftReturnsEnabled = !!currentPortalStore.setting?.enableGiftReturns;

    useEffect(() => {
        setSubTitles(['Portal', 'Content', 'Return Portal', 'Locate Order']);
    }, [setSubTitles]);

    const { selectedLocale } = useTranslation();

    const hasChanges = () => {
        const hasGiftCardEntryChanges = giftCardEntryRef.current?.hasChanges() ?? false;
        const hasLocateOrderEntryChanges = locateOrderEntryRef.current?.hasChanges() ?? false;
        return hasGiftCardEntryChanges || hasLocateOrderEntryChanges;
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const discardChanges = () => {
        giftCardEntryRef.current?.discardChanges();
        locateOrderEntryRef.current?.discardChanges();
    }

    const onSaveClicked = () => {
        const promised = [
            giftCardEntryRef.current?.save(),
            locateOrderEntryRef.current?.save(),
        ];

        Promise.all(promised).then(() => {
            unblockNavigation();
        })
    }

    const selectedScope = currentPortalStore.setting?.enablePortalV2
        ? "portal-edge"
        : "assistant";

    return (
        <>
            <EditorPageLayout
                toolbar={{
                    saveButton: {
                        hasChanges: hasChanges(),
                        disabled: !hasChanges(),
                        onClick: onSaveClicked
                    }
                }}
            >
                <ControlGroupWithPreview
                    hasTopBorder={false}
                    preview={<img src={giftReturnsEnabled ? LocateOrderGiftReturnPreview : LocateOrderPreview} alt="" />}
                >
                    <ListLayout>
                        <TranslationScope scope={selectedScope} locale={selectedLocale}>
                            {giftReturnsEnabled &&
                                <LocateOrderGiftCardEntry ref={giftCardEntryRef} />
                            }

                            <LocateOrderLocateOrderEntry ref={locateOrderEntryRef} />
                        </TranslationScope>
                    </ListLayout>
                </ControlGroupWithPreview>
            </EditorPageLayout>

            {isShowUnsaved &&
                <ConfirmModal
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => setIsShowUnsaved(false)
                    }}
                    okButton={{
                        title: 'Discard',
                        onClick: () => {
                            discardChanges();
                            setIsShowUnsaved(false);
                            retryNavigation();
                        }
                    }}
                />
            }
        </>
    );
});

export default LocateOrderEditorPage;